import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import ErrorOutline from '@mui/icons-material/ErrorOutline';

const functions = getFunctions(getApp(), 'asia-northeast3');
const confirmTossPayment = httpsCallable(functions, 'confirmTossPayment');

const Page = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 32px 16px 48px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 28px 22px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: #fffbea;
  color: #2c3561;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 1.2rem;
  font-weight: 800;
  color: #0f172a;
`;

const Sub = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.55;
  color: #64748b;
`;

const ErrorText = styled.p`
  margin: 0 0 20px;
  color: #b91c1c;
  font-size: 14px;
  line-height: 1.55;
  text-align: left;
`;

const BackLink = styled.button`
  width: 100%;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  background: #1d2340;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  &:hover {
    filter: brightness(1.05);
  }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  margin: 0 auto 14px;
  border: 3px solid #e2e8f0;
  border-top-color: #1d2340;
  border-radius: 50%;
  animation: spin 0.85s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const paymentKey = searchParams.get('paymentKey');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');
    if (!paymentKey || !orderId || amount === null || amount === '') {
      setError('결제 정보가 없습니다. 결제 관리에서 다시 시도해 주세요.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await confirmTossPayment({
          paymentKey,
          orderId,
          amount: Number(amount),
        });
        if (!cancelled) {
          navigate('/payment', { replace: true, state: { paid: true } });
        }
      } catch (e) {
        const msg =
          e?.message ||
          e?.details ||
          '결제 확인 중 오류가 발생했습니다. 결제가 중복되었는지 토스 대시보드에서 확인해 주세요.';
        if (!cancelled) setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  if (error) {
    return (
      <Page>
        <Card>
          <IconWrap style={{ background: '#fef2f2', color: '#dc2626' }}>
            <ErrorOutline style={{ fontSize: 30 }} aria-hidden />
          </IconWrap>
          <Title>결제 확인 실패</Title>
          <ErrorText>{error}</ErrorText>
          <BackLink type="button" onClick={() => navigate('/payment')}>
            결제 관리로 돌아가기
          </BackLink>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <IconWrap>
          <HourglassEmpty style={{ fontSize: 30 }} aria-hidden />
        </IconWrap>
        <Title>결제 확인 중</Title>
        <Sub>잠시만 기다려 주세요. 완료되면 결제 관리 페이지로 이동합니다.</Sub>
        <Spinner aria-hidden />
      </Card>
    </Page>
  );
};

export default PaymentSuccess;
